<template>
  <section class="gs-link-hero">
    <div class="gs-link-hero__body">
      <p class="gs-link-hero__eyebrow">{{ heroLabel }}</p>
      <div v-if="link" class="gs-link-hero__inline">
        <span class="gs-link-hero__value" :title="link">{{ link }}</span>
        <button type="button" class="gs-link-hero__copy" aria-label="Copy affiliate link" title="Copy affiliate link" @click="copyLink()">
          <gs-icon name="copy" size="16" />
        </button>
      </div>
      <p v-else class="gs-link-hero__empty">No affiliate link yet</p>
    </div>

    <div class="gs-link-hero__actions gs-refresh-controls">
      <gs-name-select
        v-if="hasPicker"
        class="gs-link-hero__picker"
        size="full"
        :value="selectedId"
        :options="linkOptions"
        :placeholder="$t('frontend_resource_affiliate_link')"
        @change="$emit('select', $event)"
      />

      <gs-button
        @click="copyLink()"
        variant="primary"
        :disabled="!link"
        :title="$t('frontend_resource_copy_affiliate_link')"
      >
        <gs-icon name="copy" size="16" />
        Copy link
      </gs-button>

      <gs-button
        variant="outline"
        :disabled="!link"
        title="Open this link in a new tab"
        @click="requestOpen()"
      >
        <gs-icon name="external-link" size="16" />
        Open
      </gs-button>
    </div>
  </section>
</template>

<script>
import { affiliateLinkId } from './portalSections';
import { copyAsset } from './copyAsset';

export default {
  name: 'LinkHero',
  props: {
    link: {
      type: String,
      default: '',
    },
    links: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    linkOptions() {
      return (this.links || []).map((link, index) => ({
        value: affiliateLinkId(link, index),
        text: link.name || `Link ${index + 1}`,
      }));
    },
    hasPicker() {
      return this.linkOptions.length > 1;
    },
    selectedOption() {
      return this.linkOptions.find(option => option.value === this.selectedId);
    },
    heroLabel() {
      return this.selectedOption ? this.selectedOption.text : 'Your affiliate link';
    },
  },
  methods: {
    requestOpen() {
      this.$emit('open');
    },
    async copyLink() {
      try {
        await copyAsset({ text: this.link }, value => this.$copyText(value));
        this.$emit('copy');
      } catch (error) {
        this.$emit('error');
      }
    },
  },
};
</script>

<style scoped>
.gs-link-hero {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  padding: 16px 20px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-link-hero__body {
  flex: 1 1 320px;
  min-width: 0;
}

.gs-link-hero__eyebrow {
  margin: 0 0 4px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-link-hero__value {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-family: var(--gs-font-mono);
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.gs-link-hero__inline { display:flex; align-items:center; gap:6px; width:fit-content; max-width:100%; }
.gs-link-hero__copy { display:inline-flex; flex:none; align-items:center; justify-content:center; width:36px; height:36px; color:var(--gs-muted-foreground); background:transparent; border:0; border-radius:8px; cursor:pointer; }
.gs-link-hero__copy:hover { color:var(--gs-primary); background:var(--gs-primary-soft); }
.gs-link-hero__copy:focus-visible { outline:2px solid var(--gs-active); outline-offset:2px; }

.gs-link-hero__empty {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

.gs-link-hero__actions {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.gs-link-hero__picker {
  width: 320px;
}

@media (max-width: 640px) {
  .gs-link-hero {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px 16px;
  }

  /* The row's `flex-basis: 320px` is read as a *height* once the container
     becomes a column, which left ~320px of dead space above the controls. */
  .gs-link-hero__body {
    flex: 0 0 auto;
    flex-basis: auto;
    width: 100%;
    min-width: 0;
  }

  .gs-link-hero__value {
    font-size: 13px;
  }

  .gs-link-hero__actions {
    width: 100%;
    min-width: 0;
  }

  .gs-link-hero__picker {
    order: -1;
    width: 100%;
    min-width: 0;
  }

  .gs-link-hero__actions ::v-deep .gs-button {
    flex: 1 1 0;
    min-width: 0;
  }
}
</style>
